/*
 * --------------------------------------
 * Copyright (c) 2020 BetterCapital.US
 * --------------------------------------
 */

import superagentPromise from "superagent-promise";
import _superagent from "superagent";
import moment from "moment-timezone";

const superagent = superagentPromise(_superagent, global.Promise);
let API_ROOT = "";
if (process.env.NODE_ENV === "production") {
  API_ROOT = process.env.REACT_APP_API_ROOT;
} else {
  API_ROOT = process.env.REACT_APP_API_ROOT;
}

const responseBody = res => res.body;

// let token = null;
const tokenPlugin = req => {
  const token = localStorage.getItem("token");
  // SETTING USER CURRENT TIMEZONE
  req.set("x-user-timezone", moment.tz.guess());

  if (token) {
    req.set("authorization", `Token ${token}`);
  }
};

const omitSlug = data => {
  delete data.slug;
  return data;
};
const omitSlugAndQueryParams = data => {
  delete data.slug;
  delete data.query;
  return data;
};
const omitFiles = data => {
  delete data.files;
  return data;
};

const requests = {
  del: (url, body) =>
    superagent
      .del(`${API_ROOT}${url}`)
      .send(body)
      .use(tokenPlugin)
      .then(responseBody),
  get: url =>
    superagent
      .get(`${API_ROOT}${url}`)
      .use(tokenPlugin)
      .then(responseBody),
  put: (url, body) =>
    superagent
      .put(`${API_ROOT}${url}`, body)
      .use(tokenPlugin)
      .then(responseBody),
  post: (url, body) =>
    superagent
      .post(`${API_ROOT}${url}`, body)
      .use(tokenPlugin)
      .then(responseBody),
  postWithFile: (url, fieldname, files, fields) => {
    let req = superagent.post(`${API_ROOT}${url}`);
    // if(Array.isArray(files))
    Object.keys(fields).forEach(fieldName => {
      req.field(fieldName, fields[fieldName]);
    });
    files.forEach(file => req.attach(fieldname, file));
    return req.use(tokenPlugin).then(responseBody);
  },
  uploadFile: (url, payload, reqFieldName = "file") => {
    let req = superagent.post(`${API_ROOT}${url}`);
    const { files, ...otherParams } = payload;
    const isFileList =
      Object.prototype.toString.call(files) === "[object FileList]";
    let filesArray = [];

    if (isFileList) filesArray = Object.values(files);
    else if (Array.isArray(files)) filesArray = files;
    else filesArray.push(files);

    Object.keys(otherParams).forEach(fieldName => {
      req.field(fieldName, otherParams[fieldName]);
    });

    filesArray.forEach(file => req.attach(reqFieldName, file));

    return req.use(tokenPlugin).then(responseBody);
  },
  getFile: url =>
    superagent
      .get(`${API_ROOT}${url}`)
      .responseType("blob")
      .use(tokenPlugin),
  getFileWithParams: (url, params) =>
    superagent
      .post(`${API_ROOT}${url}`, params)
      .responseType("blob")
      .use(tokenPlugin)
};

const Auth = {
  current: () => requests.get("/user"),
  login: userData => requests.post("/users/login", userData),
  signup: userData => requests.post("/users/register", userData),
  validateToken: () => {
    return requests.get("/users/checkToken");
  },
  googleLogin: gtoken => requests.post("/users/googleLogin", { token: gtoken }),
  activateUser: userData => {
    return requests.post("/users/checkactivationcode", userData);
  },
  forgotpassword: userData => {
    return requests.post("/users/forgotpassword", userData);
  },
  resetpassword: userData => {
    return requests.post("/users/resetpassword", userData);
  },
  userDetail: () => {
    return requests.get("/users/detail");
  },
  updateUserDetail: userData => {
    return requests.put("/users/detail", userData);
  },
  saveInvestorProfile: userData => {
    return requests.post("/users/saveInvestorProfile", userData);
  },
  updateInvestorProfile: userData => {
    return requests.post("/users/updateInvestorProfile", userData);
  },

  resendActivationEmail: email =>
    requests.post("/users/resendActivationEmail", { email: email }),

  saveFilter: userData => {
    return requests.post("/users/save_filter", { data: userData });
  }
};

const AssetRequests = {
  assetListing: query => requests.get(`/users/assets_list?${query}`),
  saveAsset: assetData => {
    return requests.post("/assets/addProperty", { data: assetData });
  },
  addAssetAndFinancials: assetData => {
    return requests.post("/assets/addAssetAndFinancials", { data: assetData });
  },
  assetDetailing: asset_id =>
    requests.get(`/assets/fetchAssetDetail/${asset_id}`),

  getBasicInfo: slug => requests.get(`/assets/${slug}/getBasicInfo`),
  updateBasicInfo: assetData =>
    requests.put(`/assets/${assetData.slug}/editBasicInfo`, {
      data: omitSlug(assetData)
    }),

  getInvestmentInfo: slug => requests.get(`/assets/${slug}/getInvestmentInfo`),
  updateInvestmentInfo: assetData =>
    requests.put(`/assets/${assetData.slug}/editInvestmentInfo`, {
      data: omitSlug(assetData)
    }),

  //Get Bank details of asset
  getAccountDetail: slug => requests.get(`/assets/${slug}/accountDetails`),
  deleteAsset: slug => requests.del(`/assets/${slug}`),
  getMedias: slug => requests.get(`/assets/${slug}/medias`),
  addMedias: data =>
    requests.uploadFile(`/assets/${data.slug}/medias/add`, data, "medias"),
  deleteMedia: data =>
    requests.del(`/assets/${data.slug}/medias/${data.fileId}/delete`),

  getAssetValuation: data => requests.get(`/assets/${data.slug}/valuation`),
  getAssetCashflow: data => requests.get(`/assets/${data.slug}/cashflow`),
  getMonthlyOverallCashflow: data =>
    requests.get(`/users/monthlyOverallCashflow`),
  getAssetMonthwiseExpenses: data =>
    requests.get(`/assets/${data.slug}/expenses/monthwise`),
  getAssetMonthwiseIncomes: data =>
    requests.get(`/assets/${data.slug}/incomes/monthwise`),
  hasAsset: slug => requests.get(`/assets/${slug}/hasAsset`),
  getAssetStats: data => requests.get(`/assets/${data.slug}/assetStats`),
  // getTotalMonthlyStats: () => requests.get(`/users/totalMonthlyStats`),
  getDashboardStats: data =>
    requests.get(`/users/dashboardStats?${data.query}`),

  exportToExcel: data =>
    requests
      .getFileWithParams(`/assets/${data.slug}/exportToExcel`, {
        data: omitSlug(data)
      })
      .then(responseBody),
  closeAsset: data =>
    requests.post(`/assets/${data.slug}/close`, {
      data: omitSlug(data)
    }),

  getMonthlyGraph: data => requests.get(`/assets/${data.slug}/monthlyGraph`),
  getYearlyGraph: data => requests.get(`/assets/${data.slug}/yearlyGraph`),
  getCashFlowGraph: data =>
    requests.post(`/assets/${data.slug}/cashFlowGraph`, {
      data: omitSlug(data)
    }),
  updateIncomeGoal: data => requests.put(`/users/incomeGoal`, data),

  getChecklist: slug => requests.get(`/assets/${slug}/checklist`),

  bulkAddAsset: data => requests.uploadFile(`/assets/bulkAdd`, data.file),
  downloadBulkAddCSV: data =>
    requests.getFile(`/assets/sampleBulkAddCsv`).then(responseBody),

  addEntity: data => requests.post("/users/entities", data),
  getEntities: () => requests.get("/users/entities")
};

const Financial = {
  addFinancialToAsset: assetData =>
    requests.post(`/assets/${assetData.slug}/financial`, {
      data: omitSlug(assetData)
    }),
  getAssetFinancials: slug => requests.get(`/assets/${slug}/financial`),
  updateAssetFinancial: data =>
    requests.put(`/assets/${data.slug}/financial`, { data: omitSlug(data) }),
  deleteAssetFinancial: data =>
    requests.del(`/assets/${data.slug}/financial/${data.id}`)
};

const Tenent = {
  getAssetTenents: tenentData =>
    requests.get(`/assets/${tenentData.slug}/tenents/get`),
  addAssetTenent: tenentData =>
    requests.post(`/assets/${tenentData.slug}/tenents/add`, {
      data: omitSlug(tenentData)
    }),
  updateAssetTenent: tenentData =>
    requests.put(`/assets/${tenentData.slug}/tenents/update`, {
      data: omitSlug(tenentData)
    }),
  deleteAssetTenent: tenentData =>
    requests.del(`/assets/${tenentData.slug}/tenents/delete`, {
      data: omitSlug(tenentData)
    })
};

const Contact = {
  getAssetContacts: contactData =>
    requests.get(`/assets/${contactData.slug}/contacts/get`),
  addAssetContact: contactData =>
    requests.post(`/assets/${contactData.slug}/contacts/add`, {
      data: omitSlug(contactData)
    }),
  updateAssetContact: contactData =>
    requests.put(`/assets/${contactData.slug}/contacts/update`, {
      data: omitSlug(contactData)
    }),
  deleteAssetContact: contactData =>
    requests.del(`/assets/${contactData.slug}/contacts/delete`, {
      data: omitSlug(contactData)
    }),
  getAllContacts: () => requests.get(`/users/contacts`),
  addContact: contactData => requests.post(`/users/contacts`, contactData),
  updateContact: contactData =>
    requests.put(`/users/contacts/${contactData.slug}`, omitSlug(contactData)),
  deleteContact: slug => requests.del(`/users/contacts/${slug}`)
};

const Transaction = {
  getAllTransactions: data => requests.get(`/users/transactions?${data.query}`),
  getTransactions: data =>
    requests.get(`/assets/${data.slug}/transactions?${data.query}`),
  getTransactionsFromBank: data =>
    requests.get(
      `/users/transactions/bank/${data.account_id}?days=${data.days_to_fetch}`
    ),
  saveTransaction: data => {
    return requests.postWithFile(
      `/assets/${data.slug}/transactions/addTransaction?${data.query}`,
      "files",
      data.files,
      omitFiles(data)
    );
  },
  saveTransactions: data =>
    requests.post(`/assets/${data.slug}/transactions/add?${data.query}`, {
      data: omitSlugAndQueryParams(data)
    }),
  updateTransaction: data =>
    requests.put(`/assets/${data.slug}/transactions/update?${data.query}`, {
      data: omitSlugAndQueryParams(data)
    }),
  deleteMultiTransaction: data =>
    requests.del(`/assets/${data.slug}/transactions/multidelete`, {
      data: omitSlug(data.data)
    }),
  deleteTransaction: data =>
    requests.del(`/assets/${data.slug}/transactions/delete?${data.query}`, {
      data: omitSlugAndQueryParams(data)
    }),
  downloadSampleCSV: data =>
    requests.getFile(`/assets/${data.slug}/sampleCsv`).then(responseBody),
  downloadPastSampleXLSX: data =>
    requests.getFile(`/assets/${data.slug}/pastSampleCsv`).then(responseBody),
  markAllPaid: data =>
    requests.put(`/assets/${data.slug}/transactions/markAllPaid`),
  removeAllDuplicates: data =>
    requests.put(`/assets/${data.slug}/transactions/removeAllDuplicates`)
};

const User = {
  getOverallCashflow: data =>
    requests.post(`/users/overallCashflow`, {
      data: omitSlug(data)
    }),
  runReport: data =>
    requests.post(`/users/runReport`, {
      data
    })
};

const Mortgage = {
  saveMortgage: data =>
    requests.post(`/assets/${data.slug}/mortgage/add`, data),
  getMortgage: data => requests.get(`/assets/${data}/mortgage/get`)
};
const Attachments = {
  saveAttachments: (data, params) =>
    requests.postWithFile(
      `/assets/${data.slug}/attachments/add`,
      data.fileName,
      data.files,
      params
    ),
  getAttachments: slug => requests.get(`/assets/${slug}/attachments`),
  getAttachmentUrl: data =>
    requests.get(`/assets/${data.slug}/attachments/${data._id}`),
  deleteAttachment: data =>
    requests.del(`/assets/${data.slug}/attachments/${data.id}/delete`),
  addTags: data =>
    requests.put(`/assets/${data.slug}/attachments/${data.id}/addTags`, {
      tags: data.tags
    }),
  requestDocument: data =>
    requests.post(`/assets/${data.slug}/requestDocument`, omitSlug(data))
};

const Rets = {
  lookupAsset: address =>
    requests.get(`/assets/lookupAsset?address=${address}`),
  lookupParcel: parcelID =>
    requests.get(`/assets/lookupParcel?parcelID=${parcelID}`)
};

const Account = {
  getAccounts: () => requests.get(`/accounts`),
  addAccount: plaidData => requests.post(`/accounts/add`, plaidData),
  deleteAccount: data => requests.del(`/accounts/${data._id}`),
  addAccountFilter: data =>
    requests.post(`/accounts/${data.accountId}/filters`, data),
  getAccountFilters: accountId =>
    requests.get(`/accounts/${accountId}/filters`),
  getAccountFilter: (accountId, filterId) =>
    requests.get(`/accounts/${accountId}/filters/${filterId}`),
  deleteAccountFilter: (accountId, filterId) =>
    requests.post(`/accounts/${accountId}/filters/${filterId}`),
  importBankTransaction: data =>
    requests.post(`/accounts/importTransaction`, data)
};

// const setAuthToken = (_token) => { token = _token; }

const CMSContent = {
  getPolicyContent: () => requests.get(`/cms/getPolicyContent`),
  getFaqContent: () => requests.get(`/cms/getPolicyContent`),
  getAboutusContent: () => requests.get(`/cms/getPolicyContent`),
  saveFeedback: feedback => requests.post(`/cms/saveFeedback`, feedback)
};

const Resources = {
  getResources: query => requests.get(`/cms/resources?type=${query}`),
  reserveResource: resource =>
    requests.put(`/cms/resources/${resource}/reserve`),
  getResourceStats: () => requests.get(`/cms/resources/stats`),

  getMyResources: query =>
    requests.get(
      `/cms/resources/myresources?type=${query.type}&userId=${query.userId}`
    ),
  getMyResourceStats: query =>
    requests.get(`/cms/resources/myresources/stats?userId=${query.userId}`),
  postResource: data => requests.post(`/cms/resources/create`, data),
  uploadFile: data => requests.uploadFile("/cms/resources/uploadFile", data),

  getViewResource: slug => requests.get(`/cms/resources/${slug}/get`)
  // updateResource
};

const Recommendations = {
  getRecommendations: () => requests.get(`/cms/recommendations`)
};

const Notifications = {
  getNotications: () => requests.get(`/notifications/`)
  // markNotificationAsCompleted: (data) => requests.put(`/notifications/${data.notiId}`,data),
  // addNotification: (data) => requests.post(`/notifications/add`, data),
};

const Logs = {
  saveAssetNotFoundLog: address =>
    requests.post("/users/saveNotFoundAssetLog", { address })
};

const Invite = {
  sendInvite: (slug, personalNote) =>
    requests.post(`/users/contacts/${slug}/send_invite`, {
      personal_note: personalNote
    })
};

const Deal = {
  addDeal: data => requests.put(`/deals/${data.slug}`, { ...data }),
  getDeals: type => requests.get(`/deals?type=${type}`),
  getMyDeals: query => requests.get(`/deals/mydeals?userId=${query.userId}`),
  transferAsset: data =>
    requests.put(`/deals/${data.slug}/transfer`, { ...data })
};

const PropertyManagement = {
  uploadFile: data =>
    requests.uploadFile(
      `/assets/myassetname/importManagementTransaction?type=${data.parseType}`,
      data.file
    )
};

const Reminder = {
  getReminders: () => requests.get(`/reminders/`),
  updateReminder: data => requests.put(`/reminders/${data.remId}`, data),
  addReminder: data => requests.post(`/reminders/add`, data)
};

const Partner = {
  signup: data => requests.post(`/users/partners/add`, data),
  updatePartner: data =>
    requests.put(`/users/partners/${data.slug}/update`, data),
  getPartner: slug => requests.get(`/users/partners/${slug}`),
  listPartners: query => requests.get(`/users/partners?type=${query}`),
  submitTestimonial: data =>
    requests.put(
      `/users/partners/${data.partner_slug}/contacts/${
        data.contact_slug
      }/testimonial`,
      data
    ),
  grantPermission: data =>
    requests.put(`/users/partners/${data.partner_slug}/grant`, data)
};

const Document = {
  getAllDocuments: queryParams =>
    requests.get(`/users/documents?${queryParams}`),
  uploadDocuments: (data, params) =>
    requests.postWithFile(
      `/users/documents`,
      data.fileName,
      data.files,
      data.params
    ),
  deleteDocument: documentID => requests.del(`/users/documents/${documentID}`),
  updateDocument: (data, id) => requests.put(`/users/documents/${id}`, data)
};

export {
  Auth,
  // setAuthToken,
  AssetRequests,
  Rets,
  Attachments,
  Account,
  CMSContent,
  Notifications,
  Transaction,
  User,
  Mortgage,
  Contact,
  Tenent,
  Financial,
  Resources,
  Recommendations,
  Logs,
  Invite,
  Deal,
  PropertyManagement,
  Reminder,
  Partner,
  Document
};
